import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PeopleIcon from '@mui/icons-material/People';
import * as d3 from 'd3';

const CrowdCameraEntityDrawer = ({ entityDetails, chartRef }) => {
  // Mock crowd camera data
  const crowdData = {
    cameras: [
      {
        id: 'CROWD-CAM-001',
        location: 'Stadium Entrance A',
        status: 'Active',
        lastDetection: '2024-01-15T10:30:00Z',
        detections: {
          people: 156,
          density: 'High',
          alerts: 2
        }
      },
      {
        id: 'CROWD-CAM-002', 
        location: 'Concourse Area',
        status: 'Active',
        lastDetection: '2024-01-15T10:28:00Z',
        detections: {
          people: 89,
          density: 'Medium',
          alerts: 0
        }
      }
    ],
    totalDetections: {
      people: 245,
      averageDensity: 'Medium',
      alerts: 2
    },
    // Time-series data for last 12 hours (24 intervals of 30 minutes each)
    timeSeriesData: [
      { time: '00:00', density: 15, people: 45 },
      { time: '00:30', density: 12, people: 36 },
      { time: '01:00', density: 8, people: 24 },
      { time: '01:30', density: 5, people: 15 },
      { time: '02:00', density: 3, people: 9 },
      { time: '02:30', density: 2, people: 6 },
      { time: '03:00', density: 1, people: 3 },
      { time: '03:30', density: 1, people: 3 },
      { time: '04:00', density: 2, people: 6 },
      { time: '04:30', density: 3, people: 9 },
      { time: '05:00', density: 5, people: 15 },
      { time: '05:30', density: 8, people: 24 },
      { time: '06:00', density: 12, people: 36 },
      { time: '06:30', density: 18, people: 54 },
      { time: '07:00', density: 25, people: 75 },
      { time: '07:30', density: 35, people: 105 },
      { time: '08:00', density: 45, people: 135 },
      { time: '08:30', density: 55, people: 165 },
      { time: '09:00', density: 65, people: 195 },
      { time: '09:30', density: 75, people: 225 },
      { time: '10:00', density: 85, people: 255 },
      { time: '10:30', density: 90, people: 270 },
      { time: '11:00', density: 88, people: 264 },
      { time: '11:30', density: 82, people: 246 }
    ]
  };

  useEffect(() => {
    if (chartRef.current && crowdData.timeSeriesData.length > 0) {
      renderCrowdDensityBarChart();
    }
  }, [crowdData]);

  const renderCrowdDensityBarChart = () => {
    // Clear previous chart
    d3.select(chartRef.current).selectAll('*').remove();

    const width = 400;
    const height = 300;
    const margin = { top: 20, right: 20, bottom: 60, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const data = crowdData.timeSeriesData;

    // Scales
    const xScale = d3.scaleBand()
      .domain(data.map(d => d.time))
      .range([0, chartWidth])
      .padding(0.1);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.density)])
      .range([chartHeight, 0]);

    // Color scale based on density levels
    const colorScale = d3.scaleLinear()
      .domain([0, 30, 60, 90])
      .range(['#4CAF50', '#FF9800', '#F44336', '#9C27B0']);

    // X-axis
    svg.append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .style('font-size', '10px');

    // Y-axis
    svg.append('g')
      .call(d3.axisLeft(yScale))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .text('Density Level')
      .style('font-size', '12px');

    // Bars
    svg.selectAll('rect')
      .data(data)
      .enter()
      .append('rect')
      .attr('x', d => xScale(d.time))
      .attr('y', d => yScale(d.density))
      .attr('width', xScale.bandwidth())
      .attr('height', d => chartHeight - yScale(d.density))
      .attr('fill', d => colorScale(d.density))
      .attr('stroke', 'white')
      .attr('stroke-width', 1)
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('opacity', 0.8);
        
        // Show tooltip
        const tooltip = d3.select(chartRef.current)
          .append('div')
          .attr('class', 'tooltip')
          .style('opacity', 0)
          .style('position', 'absolute')
          .style('background', 'rgba(0, 0, 0, 0.8)')
          .style('color', 'white')
          .style('padding', '8px')
          .style('border-radius', '4px')
          .style('font-size', '12px')
          .style('pointer-events', 'none');

        tooltip.transition()
          .duration(200)
          .style('opacity', 0.9);
        
        tooltip.html(`
          <strong>Time: ${d.time}</strong><br/>
          Density: ${d.density}%<br/>
          People: ${d.people}
        `)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 28) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('opacity', 1);
        
        d3.select(chartRef.current).selectAll('.tooltip').remove();
      });

    // Add title
    svg.append('text')
      .attr('x', chartWidth / 2)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Crowd Density - Last 12 Hours');
  };

  return (
    <>
      {/* Crowd Camera Data Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Crowd Monitoring Cameras
            </Typography>
          </Box>
          
          {/* Camera List */}
          <List dense>
            {crowdData.cameras.map((camera, index) => (
              <ListItem key={camera.id} divider>
                <ListItemIcon>
                  <CameraAltIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1">
                        {camera.id}
                      </Typography>
                      <Chip 
                        label={camera.status} 
                        size="small" 
                        color={camera.status === 'Active' ? 'success' : 'error'}
                        variant="outlined" 
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Location: {camera.location}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Last Detection: {new Date(camera.lastDetection).toLocaleString()}
                      </Typography>
                      <Box display="flex" gap={2} sx={{ mt: 1 }}>
                        <Chip 
                          label={`${camera.detections.people} people`} 
                          size="small" 
                          variant="outlined" 
                        />
                        <Chip 
                          label={`${camera.detections.density} density`} 
                          size="small" 
                          color={camera.detections.density === 'High' ? 'error' : camera.detections.density === 'Medium' ? 'warning' : 'success'}
                          variant="outlined" 
                        />
                        <Chip 
                          label={`${camera.detections.alerts} alerts`} 
                          size="small" 
                          color="warning"
                          variant="outlined" 
                        />
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>

          {/* Total Detections Summary */}
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Crowd Summary (Last 24h)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color="primary">
                    {crowdData.totalDetections.people}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Total People
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color="warning.main">
                    {crowdData.totalDetections.averageDensity}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Avg Density
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box textAlign="center">
                  <Typography variant="h6" color="error.main">
                    {crowdData.totalDetections.alerts}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Alerts
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Crowd Density Chart */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Crowd Density Timeline
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Crowd density levels over the last 12 hours (30-minute intervals)
          </Typography>
          <Box 
            ref={chartRef}
            sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '320px'
            }}
          />
        </CardContent>
      </Card>
    </>
  );
};

export default CrowdCameraEntityDrawer; 